import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/api";
import Spinner from "../../components/ui/Spinner";
import MessageBox from "../../components/ui/MessageBox";
import Card from "../../components/ui/Card";
import InputWithIcon from "../../components/ui/InputWithIcon";
import Button from "../../components/ui/Button";

const AddEditSupplierPage = ({ setView, supplierId, setSupplierId }) => {
  const [formData, setFormData] = useState({
    Vname: "",
    Address1: "",
    City: "",
    Mobile: "",
    Mail: "",
    VType: "supplier",
    Sp_SqlAction: supplierId ? "update" : "insert",
    Vid: supplierId || 0,
    VCode: "",
    State: "",
    StateCode: "",
    Pincode: "",
    GST: "-",
    Phone: "",
    Active: 1,
    ComId: "0",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [stateList, setStateList] = useState([]);

  // --- Fetch Supplier for Edit ---
  useEffect(() => {
    if (supplierId && stateList.length > 0) {
      setLoading(true);
      api
        .getSupplierForEdit(supplierId)
        .then((data) => {
          const supplier = data.Table?.[0];
          if (supplier) {
            let correctStateCode = "";
            let correctStateName = supplier.State || "";

            const byCode = stateList.find(
              (s) => s.StateCode === supplier.StateCode
            );
            const byName = stateList.find(
              (s) =>
                s.StateName.toLowerCase() ===
                (supplier.State || "").toLowerCase()
            );

            if (byCode) {
              correctStateCode = byCode.StateCode;
              correctStateName = byCode.StateName;
            } else if (byName) {
              correctStateCode = byName.StateCode;
              correctStateName = byName.StateName;
            } else if (supplier.State) {
              correctStateCode = supplier.StateCode || "custom";
              correctStateName = supplier.State;

              setStateList((prev) => {
                if (!prev.some((s) => s.StateCode === correctStateCode)) {
                  return [
                    ...prev,
                    { StateCode: correctStateCode, StateName: correctStateName },
                  ];
                }
                return prev;
              });
            }

            setFormData((prev) => ({
              ...prev,
              ...supplier,
              Sp_SqlAction: "update",
              Vid: supplier.vid || supplier.Vid,
              Active: supplier.Active ?? 1,
              State: correctStateName,
              StateCode: correctStateCode,
            }));
          }
        })
        .catch(() => setError("Failed to fetch supplier details."))
        .finally(() => setLoading(false));
    }
  }, [supplierId, stateList]);

  // --- Fetch State List ---
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const UsrName = sessionStorage.getItem("username") || "admin";
        const res = await api.getpartydetailsAPI(UsrName);

        if (res.Table) {
          setStateList(res.Table);
        } else if (res.GetpartydetailsAPIjs?.Table) {
          setStateList(res.GetpartydetailsAPIjs.Table);
        } else {
          setStateList([]);
        }
      } catch (err) {
        console.error("Failed to fetch states", err);
      }
    };

    fetchStates();
  }, []);

  // --- Handle Form Changes ---
  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [id]: checked ? 1 : 0 }));
      return;
    }

    if (id === "Mobile") {
      const regex = /^\d{0,10}$/;
      if (regex.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [id]: value,
          VCode: value,
          Phone: value,
        }));
        setMobileError(
          value.length > 0 && value.length < 10
            ? "Mobile number must be 10 digits."
            : ""
        );
      }
      return;
    }

    if (id === "State") {
      const selected = stateList.find((st) => st.StateCode === value);
      setFormData((prev) => ({
        ...prev,
        State: selected?.StateName || "",
        StateCode: value,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // --- Submit ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (formData.Mobile.length !== 10) {
      setMobileError("Mobile number must be exactly 10 digits.");
      return;
    }

    setMobileError("");
    setLoading(true);

    try {
      const payload = {
        ...formData,
        State: formData.State || "",
        StateCode: formData.StateCode || "",
      };

      const result = await api.saveSupplier(payload);
      const responseTable = result.DMLSupplierOrimShortAPI?.Table || result.Table;

      if (responseTable && responseTable[0].Status === "Success") {
        setSuccess(responseTable[0].Message || "Supplier saved successfully!");
        setTimeout(() => {
          setView("suppliers");
          setSupplierId(null);
        }, 2000);
      } else {
        setError(responseTable?.[0]?.Message || "Failed to save supplier.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && supplierId) return <Spinner />;

  return (
     <div className="w-full px-0 sm:max-w-4xl sm:pl-0 sm:pr-4 sm:mx-0">
       <Card className="w-full px-0 sm:px-6">
        <h2 className="text-2xl font-bold text-center mb-6">
       {supplierId ? "Edit Supplier" : "Add New Supplier"}
       </h2>

        
        <form onSubmit={handleSubmit} noValidate className="w-full">
          {error && <MessageBox message={error} onDismiss={() => setError("")} />}
          {success && <MessageBox message={success} type="success" />}

          <div className="mt-4 space-y-8">
            {/* Contact Info */}
            <div>
              <h3 className="pb-2 mb-4 text-lg font-semibold text-gray-700 border-b">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-0 sm:gap-x-6">
                <InputWithIcon
                  label="Supplier Name*"
                  id="Vname"
                  value={formData.Vname}
                  onChange={handleChange}
                  required
                />
                <InputWithIcon
                  label="Mobile Number*"
                  id="Mobile"
                  value={formData.Mobile}
                  onChange={handleChange}
                  required
                  error={mobileError}
                  readOnly={!!supplierId}
                />
                <InputWithIcon
                  label="Email Address"
                  id="Mail"
                  type="email"
                  value={formData.Mail}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Address Info */}
            <div>
              <h3 className="pb-2 mb-4 text-lg font-semibold text-gray-700 border-b">
                Address Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-0 sm:gap-x-6">
                <div className="md:col-span-2">
                  <label htmlFor="Address1" className="block mb-1 font-semibold">
                    Address*
                  </label>
                  <textarea
                    id="Address1"
                    value={formData.Address1}
                    onChange={handleChange}
                    rows={4}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your address"
                  />
                </div>

                <InputWithIcon
                  label="City"
                  id="City"
                  value={formData.City}
                  onChange={handleChange}
                />
                <div>
                  <label htmlFor="State" className="block mb-1 font-semibold">
                    State
                  </label>
                  <select
                    id="State"
                    value={formData.StateCode || ""}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select State --</option>
                    {stateList.map((s) => (
                      <option key={s.StateCode} value={s.StateCode}>
                        {s.StateName}
                      </option>
                    ))}
                  </select>
                </div>
                <InputWithIcon
                  label="Pincode"
                  id="Pincode"
                  value={formData.Pincode}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Active Checkbox */}
            <div>
              <label className="inline-flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="Active"
                  checked={formData.Active === 1}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded"
                />
                <span className="text-gray-700 font-semibold">Active</span>
              </label>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-4 pt-6 mt-10 border-t">
            <Button
              type="submit"
              disabled={loading}
              className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600"
            >
              {loading ? "Saving..." : "Save Supplier"}
            </Button>

            <Button
              type="button"
              onClick={() => {
                setView("suppliers");
                setSupplierId(null);
              }}
              className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddEditSupplierPage;
